import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { Color, ColorV2 } from "config/colors";
import { CircularProgress } from "@material-ui/core";
import { ReactComponent as Image } from "images/icons/svg/image.svg";

type Props = React.HTMLAttributes<HTMLDivElement> & {
  name: string;
  title?: string;
  size?: string;
  color?: Color | ColorV2;
  backgroundColor?: Color | ColorV2;
  padding?: string;
  disabled?: boolean;
  hover?: boolean;
  square?: boolean;
  width?: string;
  marginTop?: string;
};

/**
 * CustomIcon component for dynamically importing and rendering SVG icons.
 */
export const CustomIcon: React.FC<Props> = ({
  name,
  title,
  ...rest
}): React.ReactElement => {
  const ImportedIconRef = useRef<React.FC<
    React.SVGProps<SVGSVGElement> & {
      title?: string | undefined;
    }
  > | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect((): void => {
    setLoading(true);

    const getIcon = async (): Promise<void> => {
      try {
        const res = await import(
          `!!@svgr/webpack?-svgo,+titleProp,+ref!images/icons/svg/${name}.svg`
        );
        ImportedIconRef.current = res.default;
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    getIcon();
  }, [name]);

  const renderIcon = (): React.ReactElement | null => {
    const { current: Icon } = ImportedIconRef;

    if (loading) {
      return <CircularProgress style={{ height: "100%", width: "100%" }} />;
    }

    if (Icon) {
      return <Icon title={title} />;
    }

    return <Image />;
  };

  return (
    <IconWrapper {...rest} onMouseDown={(e) => e.preventDefault()}>
      {renderIcon()}
    </IconWrapper>
  );
};

const IconWrapper = styled.div<Omit<Props, "name">>`
  background-color: ${(props) =>
    props.disabled
      ? Color.Disabled
      : props.backgroundColor
        ? props.backgroundColor
        : "none"};
  border-radius: ${(props) => (props.square ? "8px" : "50%")};
  height: ${(props) => (props.size ? `${props.size}` : "30px")};
  margin-top: ${(props) => (props.marginTop ? `${props.marginTop}` : null)};
  padding: ${(props) => props.padding && `${props.padding}`};
  transition: opacity 0.15s;
  width: ${(props) =>
    props.width ? `${props.width}` : props.size ? `${props.size}` : "30px"};

  ${(props) =>
    !props.disabled &&
    (props.onClick || props.hover) &&
    `&:hover {
      cursor: pointer;
      opacity: 0.8;
    }`}

  svg {
    height: 100%;
    width: 100%;
  }

  svg[fill],
  svg *[fill] {
    fill: ${(props) =>
      props.disabled
        ? Color.SeaBlue600
        : props.color
          ? props.color
          : Color.PitchBlack};
    fill-opacity: initial;
  }

  svg[fill="none"],
  svg *[fill="none"] {
    fill: ${(props) =>
      props.disabled
        ? Color.SeaBlue600
        : props.color
          ? props.color
          : Color.PitchBlack};
    fill-opacity: 0;
  }

  svg[stroke],
  svg *[stroke] {
    stroke: ${(props) =>
      props.disabled
        ? Color.SeaBlue600
        : props.color
          ? props.color
          : Color.PitchBlack};
  }

  svg[stroke="none"],
  svg *[stroke="none"] {
    stroke: none;
    stroke-opacity: 0;
  }
`;
